import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  DollarSign, Building, Clock, Trash2, Eye,
  Mail, Phone, User, RefreshCw, X, CheckCircle,
  FileText, TrendingUp
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/zkteztq8_OrchardLMS%20Logo.png";

const FinancingRequestsPage = () => {
  const { user, token, API, logout, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, pending: 0, reviewed: 0 });
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    if (!isPlatformAdmin()) {
      navigate('/platform/login');
      return;
    }
    fetchRequests();
  }, [user, navigate, filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/platform/financing-requests${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
      setStats({ 
        total: response.data.total, 
        pending: response.data.pending,
        reviewed: response.data.reviewed 
      });
    } catch (error) {
      toast.error('Failed to fetch financing requests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      await axios.patch(`${API}/platform/financing-requests/${requestId}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request updated');
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status });
      }
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      await axios.delete(`${API}/platform/financing-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request deleted');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatLabel = (value) => {
    if (!value) return '-';
    return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatAmount = (amount) => {
    const amountMap = {
      'under_25k': 'Under $25,000',
      '25k_50k': '$25,000 - $50,000',
      '50k_100k': '$50,000 - $100,000',
      '100k_250k': '$100,000 - $250,000',
      '250k_500k': '$250,000 - $500,000',
      'over_500k': 'Over $500,000'
    };
    return amountMap[amount] || amount;
  };

  const formatFinancingType = (type) => {
    const typeMap = {
      'working_capital': 'Working Capital Line of Credit',
      'term_loan': 'Term Loan',
      'equipment_financing': 'Equipment Financing',
      'not_sure': 'Needs Guidance'
    };
    return typeMap[type] || type;
  };

  const navItems = [
    { path: '/platform', label: 'Dashboard' },
    { path: '/platform/tenants', label: 'Tenants' },
    { path: '/platform/messages', label: 'Messages' },
    { path: '/platform/financing-requests', label: 'Financing', badge: stats.pending },
    { path: '/platform/analytics', label: 'Analytics' }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/platform" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Orchard LMS" className="h-8 w-auto" />
              </Link>
              <nav className="hidden md:flex items-center gap-6">
                {navItems.map((item) => (
                  <Link 
                    key={item.path}
                    to={item.path} 
                    className={`text-sm font-medium transition-colors flex items-center gap-2 ${
                      item.path === '/platform/financing-requests' 
                        ? 'text-white' 
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {item.label}
                    {item.badge > 0 && (
                      <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white">{user?.name || user?.email}</p>
                <p className="text-xs text-gray-400">Platform Admin</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-300 hover:text-white">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Financing Requests</h1>
            <p className="text-gray-400">Applications from the financing page</p>
          </div>
          <Button variant="outline" onClick={fetchRequests} className="border-gray-600 text-gray-300 hover:bg-gray-800">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Requests</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
                <FileText className="h-8 w-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Pending Review</p>
                  <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Reviewed</p>
                  <p className="text-2xl font-bold text-emerald-500">{stats.reviewed}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4">
          {['all', 'pending', 'reviewed', 'contacted', 'approved', 'declined'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-primary' : 'text-gray-400 hover:text-white hover:bg-gray-800'}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Requests List */}
          <div className="lg:col-span-1">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No financing requests found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-700">
                    {requests.map((req) => (
                      <div
                        key={req.id}
                        onClick={() => {
                          setSelectedRequest(req);
                          if (req.status === 'pending') {
                            updateStatus(req.id, 'reviewed');
                          }
                        }}
                        className={`p-4 cursor-pointer transition-colors ${
                          selectedRequest?.id === req.id 
                            ? 'bg-gray-700' 
                            : 'hover:bg-gray-750 hover:bg-gray-700/50'
                        } ${req.status === 'pending' ? 'border-l-2 border-amber-500' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <p className={`font-medium truncate ${req.status === 'pending' ? 'text-white' : 'text-gray-300'}`}>
                            {req.business_name}
                          </p>
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                            {formatDate(req.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 truncate">{req.first_name} {req.last_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                            {formatFinancingType(req.financing_type)}
                          </span>
                          <span className="text-xs text-emerald-400">{formatAmount(req.amount_requested)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Request Detail */}
          <div className="lg:col-span-2">
            {selectedRequest ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="border-b border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Building className="h-5 w-5 text-gray-400" />
                        {selectedRequest.business_name}
                      </CardTitle>
                      <p className="text-gray-400 text-sm mt-1">
                        {selectedRequest.first_name} {selectedRequest.last_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="border-gray-600 text-gray-300">
                            Status: {formatLabel(selectedRequest.status)}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => updateStatus(selectedRequest.id, 'pending')}>
                            Pending
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(selectedRequest.id, 'reviewed')}>
                            Reviewed
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(selectedRequest.id, 'contacted')}>
                            Contacted
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(selectedRequest.id, 'approved')}>
                            Approved
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(selectedRequest.id, 'declined')}>
                            Declined
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteRequest(selectedRequest.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-400 mb-3">Contact Information</h3>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-gray-300">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <a href={`mailto:${selectedRequest.email}`} className="hover:text-primary">
                            {selectedRequest.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <a href={`tel:${selectedRequest.phone}`} className="hover:text-primary">
                            {selectedRequest.phone}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Business Info */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-400 mb-3">Business Details</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Type:</span>
                          <span className="text-gray-300">{formatLabel(selectedRequest.business_type)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Years in Business:</span>
                          <span className="text-gray-300">{formatLabel(selectedRequest.years_in_business)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Annual Revenue:</span>
                          <span className="text-gray-300">{formatLabel(selectedRequest.annual_revenue)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Employees:</span>
                          <span className="text-gray-300">{formatLabel(selectedRequest.number_of_employees)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Financing Request */}
                    <div className="md:col-span-2 border-t border-gray-700 pt-4">
                      <h3 className="text-sm font-medium text-gray-400 mb-3">Financing Request</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-gray-900 rounded-lg p-4">
                          <p className="text-xs text-gray-500 mb-1">Financing Type</p>
                          <p className="text-lg font-medium text-white">{formatFinancingType(selectedRequest.financing_type)}</p>
                        </div>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <p className="text-xs text-gray-500 mb-1">Amount Requested</p>
                          <p className="text-lg font-medium text-emerald-400">{formatAmount(selectedRequest.amount_requested)}</p>
                        </div>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <p className="text-xs text-gray-500 mb-1">Purpose</p>
                          <p className="text-gray-300">{formatLabel(selectedRequest.purpose)}</p>
                        </div>
                        <div className="bg-gray-900 rounded-lg p-4">
                          <p className="text-xs text-gray-500 mb-1">Credit Score Range</p>
                          <p className="text-gray-300">{formatLabel(selectedRequest.credit_score_range)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Info */}
                    {selectedRequest.additional_info && (
                      <div className="md:col-span-2 border-t border-gray-700 pt-4">
                        <h3 className="text-sm font-medium text-gray-400 mb-3">Additional Information</h3>
                        <p className="text-gray-300 whitespace-pre-wrap">{selectedRequest.additional_info}</p>
                      </div>
                    )}

                    {/* Submitted At */}
                    <div className="md:col-span-2 border-t border-gray-700 pt-4">
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Clock className="h-4 w-4" />
                        <span>Submitted: {new Date(selectedRequest.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-800 border-gray-700 h-full">
                <CardContent className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <DollarSign className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Select a request to view details</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinancingRequestsPage;
